import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Empty } from "@/components/PageHeader";
import { fmtDate, money } from "@/lib/format";
import { CheckCircle } from "@phosphor-icons/react";

const SECTIONS = ["b2b", "b2cs", "cdnr", "hsn"];

export const Gstr1Tab = ({ gstr1, preview, arn, setArn, onDownload, onPreview, onFile }) => (
  <div className="space-y-4" data-testid="gstr1-view">
    <div className="flex flex-wrap items-center gap-2 border border-border p-3" data-testid="gstr1-export-bar">
      <Button size="sm" onClick={() => onDownload("json")} data-testid="download-gstr1-json-btn">Download offline-utility JSON</Button>
      <Button size="sm" variant="outline" onClick={onPreview} data-testid="preview-gstr1-btn">Preview schema</Button>
      {SECTIONS.map((s) => (
        <Button key={s} size="sm" variant="outline" onClick={() => onDownload("csv", s)} data-testid={`download-gstr1-csv-${s}`}>
          {s.toUpperCase()} CSV
        </Button>
      ))}
      <div className="ml-auto flex items-center gap-2">
        <Input className="h-8 w-44 text-xs" placeholder="ARN (optional)" value={arn}
          onChange={(e) => setArn(e.target.value)} data-testid="gstr1-arn-input" />
        <Button size="sm" onClick={() => onFile("GSTR-1")} data-testid="mark-gstr1-filed-btn">Mark GSTR-1 filed</Button>
      </div>
    </div>

    {preview ? (
      <div className="flex flex-wrap gap-px border border-border bg-border text-xs" data-testid="gstr1-preview">
        {[["Schema", preview.schema_version], ["Return period", preview.fp], ["GSTIN", preview.gstin],
          ["B2B counterparties", preview.sections.b2b], ["B2CS rows", preview.sections.b2cs],
          ["CDNR counterparties", preview.sections.cdnr], ["HSN rows", preview.sections.hsn],
          ["Gross turnover", money(preview.gross_turnover)]].map(([k, v]) => (
          <div key={k} className="flex-1 bg-background p-3">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{k}</p>
            <p className="num mt-0.5 font-medium">{v}</p>
          </div>
        ))}
      </div>
    ) : null}

    <div className="grid gap-px border border-border bg-border sm:grid-cols-3">
      {gstr1.sections.map((s) => (
        <div key={s.section} className="bg-background p-4">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{s.section}</p>
          <p className="num mt-1 text-lg font-semibold">{money(s.taxable)}</p>
          <p className="num text-[11px] text-muted-foreground">{s.count} records · tax {money(s.tax)}</p>
        </div>
      ))}
    </div>

    {gstr1.doc_count === 0 ? <Empty label="No outward supplies in this period" /> : (
      <>
        <div className="border border-border">
          <p className="border-b border-border px-4 py-2 font-display text-sm font-bold">B2B invoices (Table 4)</p>
          <table className="dense-table w-full">
            <thead><tr><th>Invoice</th><th>Date</th><th>Party</th><th>GSTIN</th><th>POS</th>
              <th className="text-right">Taxable</th><th className="text-right">IGST</th>
              <th className="text-right">CGST</th><th className="text-right">SGST</th><th>IRN</th></tr></thead>
            <tbody>
              {gstr1.b2b.map((r) => (
                <tr key={r.voucher_id} className="row-in">
                  <td className="num">{r.invoice_no}</td><td className="num">{fmtDate(r.date)}</td>
                  <td>{r.party_name}</td><td className="num">{r.gstin}</td><td className="num">{r.place_of_supply}</td>
                  <td className="num text-right">{money(r.taxable)}</td><td className="num text-right">{money(r.igst)}</td>
                  <td className="num text-right">{money(r.cgst)}</td><td className="num text-right">{money(r.sgst)}</td>
                  <td>{r.irn ? <CheckCircle size={14} className="text-success" /> : "—"}</td>
                </tr>
              ))}
              {gstr1.b2b.length === 0 && <tr><td colSpan={10} className="text-muted-foreground">No B2B invoices.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="border border-border">
          <p className="border-b border-border px-4 py-2 font-display text-sm font-bold">Credit / Debit notes (Table 9B — CDNR)</p>
          <table className="dense-table w-full" data-testid="gstr1-cdnr-table">
            <thead><tr><th>Note</th><th>Date</th><th>Type</th><th>Party</th><th>Against</th>
              <th className="text-right">Taxable</th><th className="text-right">IGST</th>
              <th className="text-right">CGST</th><th className="text-right">SGST</th></tr></thead>
            <tbody>
              {(gstr1.cdnr || []).map((r) => (
                <tr key={r.voucher_id} className="row-in">
                  <td className="num">{r.note_no}</td><td className="num">{fmtDate(r.date)}</td>
                  <td>{r.type === "Credit Note" ? "C" : "D"}</td><td>{r.party_name}</td>
                  <td className="num text-muted-foreground">{r.original_number}</td>
                  <td className="num text-right">{money(r.taxable)}</td><td className="num text-right">{money(r.igst)}</td>
                  <td className="num text-right">{money(r.cgst)}</td><td className="num text-right">{money(r.sgst)}</td>
                </tr>
              ))}
              {(gstr1.cdnr || []).length === 0 && <tr><td colSpan={9} className="text-muted-foreground">No credit/debit notes this period.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="border border-border">
          <p className="border-b border-border px-4 py-2 font-display text-sm font-bold">HSN summary (Table 12)</p>
          <table className="dense-table w-full">
            <thead><tr><th>HSN</th><th>UOM</th><th className="text-right">Rate</th><th className="text-right">Qty</th>
              <th className="text-right">Taxable</th><th className="text-right">IGST</th><th className="text-right">CGST</th><th className="text-right">SGST</th></tr></thead>
            <tbody>
              {gstr1.hsn_summary.map((h) => (
                <tr key={`${h.hsn}-${h.uom}-${h.gst_rate}`}>
                  <td className="num">{h.hsn}</td><td>{h.uom}</td><td className="num text-right">{h.gst_rate}%</td>
                  <td className="num text-right">{h.qty}</td><td className="num text-right">{money(h.taxable)}</td>
                  <td className="num text-right">{money(h.igst)}</td><td className="num text-right">{money(h.cgst)}</td>
                  <td className="num text-right">{money(h.sgst)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )}
  </div>
);
